import React, { FC, useEffect, useMemo } from "react";

import { UseFormReturn } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";

import { Tip } from "@api";

import {
  calculateKelly,
  formatCurrency,
  roundNumber,
  withController,
} from "@utils";

import {
  Icon,
  Notification,
  Table,
  TableData,
  TableHeading,
  TableRow,
  Text,
  Title,
} from "@ui/elements";
import { Checkbox, Control, CurrencyInput, Field, Input } from "@ui/forms";
import { Stack } from "@ui/layout";

import { usePageSettings } from "../../../PageTemplate/PageTemplate";

export type TipSaveFormFields = {
  odds: number;
  stake: number;
  useKelly: boolean;
};

const ControlledInput = withController<TipSaveFormFields>()(Input);
const ControlledCurrencyInput =
  withController<TipSaveFormFields>()(CurrencyInput);

type TipSaveFormProps = {
  tip: Tip;
  form: UseFormReturn<TipSaveFormFields>;
};

export const oddsToNumber = (odds?: string | number) => {
  if (typeof odds === "string") {
    return Number(odds.replace(",", "."));
  }
  return Number(odds);
};

export const TipSaveForm: FC<TipSaveFormProps> = ({ form, tip }) => {
  const { t } = useTranslation();
  const { currency, bankroll, kellyFraction } = usePageSettings();
  const stake = form.watch("stake");
  const odds = form.watch("odds");
  const useKelly = form.watch("useKelly");

  const kellyBetAmount = useMemo(() => {
    return Math.max(
      Number(
        calculateKelly(
          oddsToNumber(odds),
          Number(tip.probability),
          kellyFraction,
        ).toFixed(2),
      ),
      0,
    );
  }, [odds, tip.probability, kellyFraction]);

  useEffect(() => {
    if (useKelly) {
      form.setValue(
        "stake",
        roundNumber((Number(bankroll) * kellyBetAmount) / 100),
      );
    }
  }, [form.setValue, kellyBetAmount, bankroll, useKelly]);

  const potentialReturn = useMemo(() => {
    return formatCurrency(
      stake * oddsToNumber(odds),
      currency?.code,
      currency?.locale,
    );
  }, [odds, currency, stake]);

  return (
    <Stack direction="column">
      <Field>
        <Control>
          <Checkbox {...form.register("useKelly")} checked={useKelly}>
            {t("use-kelly-criterion")}
          </Checkbox>
        </Control>
      </Field>
      <Text size="small">
        <Trans i18nKey="use-kelly-criterion-explanation" />
      </Text>

      {kellyBetAmount <= 0 && useKelly && (
        <Notification color="warning">
          <Stack direction="column" gap="xxs">
            <Stack gap="xs">
              <Icon icon="Warning" />
              <Text weight="bold">{t("messages.odds-too-low")}</Text>
            </Stack>
            <Text>{t("messages.odds-too-low-info")}</Text>
          </Stack>
        </Notification>
      )}

      <Table isStriped={false}>
        <TableRow>
          <TableHeading style={{ paddingBottom: 0 }}>{t("odds")}</TableHeading>
          <TableHeading style={{ paddingBottom: 0 }}>{t("stake")}</TableHeading>
          <TableHeading style={{ paddingBottom: 0 }}>
            <Text align="right">{t("potential-return")}</Text>
          </TableHeading>
        </TableRow>
        <TableRow>
          <TableData vAlign="top">
            <ControlledInput
              name="odds"
              control={form.control}
              type="text"
              inputMode="decimal"
              className="odds-input"
              rules={{
                min: {
                  value: 1,
                  message: t("messages.odds-min-1"),
                },
                required: t("messages.odds-required"),
              }}
              min={1}
              enterKeyHint="enter"
              step="any"
            />
          </TableData>
          <TableData vAlign="top">
            <ControlledCurrencyInput
              name="stake"
              className="stake-input"
              control={form.control}
              currency={currency}
              disabled={useKelly}
              rules={{
                min: {
                  value: 1,
                  message: t("messages.stake-min-0"),
                },
                required: t("messages.stake-required"),
              }}
              min={1}
            />
          </TableData>
          <TableData isFullwidth>
            <Title align="right" size={4}>
              {potentialReturn}
            </Title>
          </TableData>
        </TableRow>
      </Table>
    </Stack>
  );
};
